import { mapFromAuditCriterionDTO } from 'mappings/auditCriteriaMapping';
import {
  AssessmentAutomations,
  AssessmentEvidenceAudit,
  AssessmentEvidenceAuditStart,
  AssessmentTaskCreate,
  AssessmentTaskCreated,
  AssessmentTaskResult,
  AuditCriteriaCompare,
  AuditCriteriaCompareRow,
  GenericAuditCriteriaSetRow,
} from 'models/auditCriteriaGenericSets';
import {
  AssessmentAutomationsDTO,
  AssessmentTaskCreateDTO,
  AssessmentTaskCreatedDTO,
  AssessmentTaskResultDTO,
  AuditCriteriaCompareDTO,
  AuditCriteriaCompareRowDTO,
  GenericAuditCriteriaSetCreateDTO,
  GenericAuditCriteriaSetRowDTO,
} from 'models/dto/auditCriteriaGenericDTO';
import { toApiDateTimeOptional } from 'utils/datetime';

// PLAN-075 Tasks 14 and 16: generic criteria sets, the copy compare, assessment tasks with the start card and its
// starts. The outcomes a set carries are mapped with the set, in auditCriteriaMapping.ts.

export function mapFromGenericAuditCriteriaSetRowDTO(dto: GenericAuditCriteriaSetRowDTO): GenericAuditCriteriaSetRow {
  const output = new GenericAuditCriteriaSetRow();
  output.auditCriteriaSetId = dto.auditCriteriaSetId;
  output.entityType = dto.entityType;
  output.subType = dto.subType ?? undefined;
  output.activeCount = dto.activeCount;
  output.modifiedOn = dto.modifiedOn ? new Date(dto.modifiedOn) : undefined;
  output.reviewedOn = dto.reviewedOn ? new Date(dto.reviewedOn) : undefined;
  output.headVersion = dto.headVersion ?? undefined;
  output.affectedEntityCount = dto.affectedEntityCount;
  output.copyCount = dto.copyCount;

  return output;
}

export function mapToGenericAuditCriteriaSetCreateDTO(
  entityType: number,
  subType: number | undefined,
  copyFromFamilySet: boolean,
): GenericAuditCriteriaSetCreateDTO {
  const output = new GenericAuditCriteriaSetCreateDTO();
  output.entityType = entityType;
  output.subType = subType ?? null;
  output.copyFromFamilySet = copyFromFamilySet;

  return output;
}

export function mapFromAuditCriteriaCompareDTO(dto: AuditCriteriaCompareDTO): AuditCriteriaCompare {
  const output = new AuditCriteriaCompare();
  output.auditCriteriaSetId = dto.auditCriteriaSetId;
  output.sourceSetId = dto.sourceSetId ?? undefined;
  output.copiedOn = dto.copiedOn ? new Date(dto.copiedOn) : undefined;
  output.sourceChangedOn = dto.sourceChangedOn ? new Date(dto.sourceChangedOn) : undefined;
  output.rows = (dto.rows ?? []).map(mapFromAuditCriteriaCompareRowDTO);

  return output;
}

function mapFromAuditCriteriaCompareRowDTO(dto: AuditCriteriaCompareRowDTO): AuditCriteriaCompareRow {
  const output = new AuditCriteriaCompareRow();
  output.lineageKey = dto.lineageKey;
  output.changeKind = dto.changeKind;
  output.copy = dto.copy ? mapFromAuditCriterionDTO(dto.copy) : undefined;
  output.source = dto.source ? mapFromAuditCriterionDTO(dto.source) : undefined;

  return output;
}

export function mapToAssessmentTaskCreateDTO(create: AssessmentTaskCreate): AssessmentTaskCreateDTO {
  const output = new AssessmentTaskCreateDTO();
  output.entityType = create.entityType;
  output.entityIds = [...create.entityIds];
  output.startDate = toApiDateTimeOptional(create.startDate) ?? null;
  output.deadline = toApiDateTimeOptional(create.deadline);
  output.assigneeId = create.assigneeId;
  output.runEvidenceAudit = create.runEvidenceAudit;

  return output;
}

export function mapFromAssessmentTaskResultDTO(dto: AssessmentTaskResultDTO): AssessmentTaskResult {
  const output = new AssessmentTaskResult();
  output.created = (dto.created ?? []).map(mapFromAssessmentTaskCreatedDTO);
  output.refused = (dto.refused ?? []).map((refused) => ({
    entityType: refused.entityType,
    entityId: refused.entityId,
    code: refused.code ?? '',
    message: refused.message ?? '',
  }));

  return output;
}

export function mapFromAssessmentTaskCreatedDTO(dto: AssessmentTaskCreatedDTO): AssessmentTaskCreated {
  const output = new AssessmentTaskCreated();
  output.entityType = dto.entityType;
  output.entityId = dto.entityId;
  output.taskId = dto.taskId;
  if (dto.evidenceAudit) {
    output.evidenceAudit = new AssessmentEvidenceAuditStart();
    output.evidenceAudit.started = dto.evidenceAudit.started;
    output.evidenceAudit.reason = dto.evidenceAudit.reason ?? undefined;
  }

  return output;
}

export function mapFromAssessmentAutomationsDTO(dto: AssessmentAutomationsDTO): AssessmentAutomations {
  const output = new AssessmentAutomations();
  output.latestAssigneeId = dto.latestAssigneeId ?? undefined;
  if (dto.evidenceAudit) {
    output.evidenceAudit = new AssessmentEvidenceAudit();
    output.evidenceAudit.criteriaCount = dto.evidenceAudit.criteriaCount ?? 0;
    output.evidenceAudit.offered = dto.evidenceAudit.offered === true;
  }

  return output;
}
